import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight, Check, Wallet, TrendingUp, Gauge, ClipboardList,
    Building2, Users, ClipboardCheck, Send, MonitorSmartphone, Zap, Sparkles,
} from "lucide-react";
import Reveal, { MaskedLine, easeOut } from "../components/Reveal";
import Marquee from "../components/Marquee";
import DashboardMockup from "../components/DashboardMockup";
import usePageMeta from "../hooks/usePageMeta";

const DESTAQUES_PLANOS = [
    { icon: Wallet, valor: "R$ 99/mês", texto: "planos a partir de", destaque: true },
    { icon: ClipboardList, valor: "Uma plataforma", texto: "toda a gestão de benefícios" },
    { icon: TrendingUp, valor: "Mais previsibilidade", texto: "para sua empresa" },
    { icon: Gauge, valor: "Mais controle", texto: "para o RH" },
];

const PASSOS = [
    { n: "01", icon: Building2, titulo: "Cadastre sua empresa" },
    { n: "02", icon: Users, titulo: "Cadastre seus colaboradores" },
    { n: "03", icon: ClipboardCheck, titulo: "Escolha os benefícios" },
    { n: "04", icon: Send, titulo: "Faça os pedidos" },
    { n: "05", icon: MonitorSmartphone, titulo: "Acompanhe tudo pela plataforma" },
];

const PORQUE = [
    { icon: Wallet, titulo: "Planos a partir de R$ 99/mês", texto: "Preço simples para a sua empresa utilizar a plataforma Smart Benefícios.", grande: true },
    { icon: ClipboardList, titulo: "Gestão centralizada", texto: "Todos os benefícios organizados em um único ambiente." },
    { icon: Zap, titulo: "Tecnologia", texto: "Uma plataforma moderna para simplificar processos." },
    { icon: Gauge, titulo: "Controle", texto: "Mais visibilidade sobre pedidos, colaboradores e benefícios." },
];

const PLANO_ITENS = [
    "Gestão de Vale-Transporte", "Vale-Refeição", "Vale-Alimentação", "Smart Pix", "Smart Libert",
    "Gestão de colaboradores", "Gestão de pedidos", "Acompanhamento de liberações", "Área do funcionário",
    "Relatórios", "Importação de colaboradores", "Controle por empresa e departamento", "Suporte Smart",
];

function Eyebrow({ children }) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border border-azul-100 bg-azul-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-azul-700">
            <span className="h-1.5 w-1.5 rounded-full bg-laranja-500" />
            {children}
        </span>
    );
}

function CardSolucao({ img, alt, titulo, texto, children, cta, href, testid }) {
    return (
        <Reveal className="h-full">
            <article className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-suave transition-all duration-300 hover:-translate-y-1 hover:border-azul-200 hover:shadow-card" data-testid={testid}>
                <div className="relative h-44 overflow-hidden">
                    <img
                        src={img}
                        alt={alt}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-azul-950/30 to-transparent" />
                </div>
                <div className="flex flex-1 flex-col p-6 md:p-7">
                    <h3 className="font-display text-2xl font-bold text-tinta">{titulo}</h3>
                    <p className="mt-2.5 text-[15px] leading-relaxed text-tinta-medio">{texto}</p>
                    {children}
                    <Link
                        to={href}
                        data-testid={`${testid}-btn`}
                        className="mt-auto inline-flex items-center gap-2 pt-6 font-display text-base font-bold text-azul-600 transition-colors hover:text-laranja-500"
                    >
                        {cta} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                </div>
            </article>
        </Reveal>
    );
}

function DestaqueCheck({ children }) {
    return (
        <li className="flex items-start gap-2 text-sm font-medium text-tinta-medio">
            <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-laranja-100">
                <Check className="h-3 w-3 text-laranja-600" strokeWidth={3} />
            </span>
            {children}
        </li>
    );
}

export default function Home() {
    usePageMeta(
        "Smart Benefícios | Gestão inteligente de benefícios",
        "Gestão de benefícios corporativos com plataforma inteligente e planos a partir de R$ 99/mês. Simplifique o Vale-Transporte, Vale-Refeição e Vale-Alimentação da sua empresa."
    );

    return (
        <>
            {/* ================= HERO ================= */}
            <section className="relative overflow-hidden bg-white pt-[112px]" data-testid="home-hero">
                <div aria-hidden="true" className="pointer-events-none absolute inset-0">
                    <div className="absolute -top-32 right-[-10%] h-[560px] w-[560px] rounded-full bg-azul-50 blur-3xl" />
                    <div className="absolute bottom-[-30%] left-[-8%] h-[420px] w-[420px] rounded-full bg-laranja-50 blur-3xl" />
                    <svg className="absolute inset-0 h-full w-full opacity-[0.35]" aria-hidden="true">
                        <defs>
                            <pattern id="grid" width="44" height="44" patternUnits="userSpaceOnUse">
                                <circle cx="1.5" cy="1.5" r="1.5" fill="#E0EBFF" />
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#grid)" />
                    </svg>
                </div>

                <div className="container-x relative z-10 grid items-center gap-14 pb-20 pt-10 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-16">
                    <div className="lg:col-span-6">
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                            <Eyebrow>Planos a partir de R$ 99/mês</Eyebrow>
                        </motion.div>

                        <h1 className="mt-6 font-display text-[2.6rem] font-extrabold leading-[1.06] tracking-tight text-tinta sm:text-6xl lg:text-[3.6rem]" data-testid="hero-title">
                            <MaskedLine delay={0.1}>Gestão de benefícios</MaskedLine>
                            <MaskedLine delay={0.22}>sem <span className="text-azul-500">complicação</span></MaskedLine>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.55, ease: easeOut }}
                            className="mt-6 max-w-xl font-display text-xl font-semibold leading-snug text-azul-900 sm:text-2xl"
                            data-testid="hero-headline"
                        >
                            Planos a partir de <span className="hl-laranja text-laranja-600">R$ 99/mês</span>.
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.65, ease: easeOut }}
                            className="mt-4 max-w-lg text-base leading-relaxed text-tinta-medio sm:text-lg"
                            data-testid="hero-texto"
                        >
                            Uma plataforma para simplificar a gestão de benefícios da sua empresa, com praticidade, controle e tecnologia.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.75, ease: easeOut }}
                            className="mt-8 inline-flex items-center gap-5 rounded-2xl border border-laranja-200 bg-laranja-50 px-6 py-4"
                            data-testid="hero-preco-card"
                        >
                            <div>
                                <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-laranja-600">a partir de</span>
                                <span className="font-display text-4xl font-extrabold tracking-tight text-laranja-500 sm:text-5xl" data-testid="hero-preco">
                                    R$ 99<span className="text-2xl">/mês</span>
                                </span>
                            </div>
                            <span className="max-w-[220px] text-[13px] font-medium leading-snug text-tinta-medio">
                                Planos para utilizar a plataforma Smart Benefícios e gerenciar os benefícios da sua empresa.
                            </span>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.85, ease: easeOut }}
                            className="mt-8 flex flex-col gap-3 sm:flex-row"
                        >
                            <a
                                href="#planos"
                                data-testid="hero-cta-conheca-planos"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-azul-500 px-7 py-3.5 text-base font-bold text-white shadow-[0_10px_28px_-8px_rgba(13,82,206,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-azul-600"
                            >
                                Conheça nossos planos <ArrowRight className="h-4 w-4" />
                            </a>
                            <Link
                                to="/contato"
                                data-testid="hero-cta-especialista"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-laranja-500 px-7 py-3.5 text-base font-bold text-white shadow-laranja transition-all duration-200 hover:-translate-y-0.5 hover:bg-laranja-600"
                            >
                                Fale com um especialista
                            </Link>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-6 lg:pl-6">
                        <DashboardMockup />
                    </div>
                </div>
            </section>

            <Marquee />

            {/* ================= PREÇO SIMPLES ================= */}
            <section className="bg-white py-20 md:py-28" data-testid="home-planos-destaques">
                <div className="container-x">
                    <div className="mx-auto max-w-3xl text-center">
                        <Reveal><Eyebrow>Preço simples</Eyebrow></Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-tinta sm:text-4xl lg:text-5xl" data-testid="planos-title">
                                Um preço simples. Toda a gestão.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.14}>
                            <p className="mt-5 text-base leading-relaxed text-tinta-medio sm:text-lg">
                                A Smart Benefícios oferece <strong className="text-azul-700">planos a partir de R$ 99/mês</strong> para a sua empresa gerenciar Vale-Transporte, Vale-Refeição e Vale-Alimentação em uma única plataforma.
                            </p>
                        </Reveal>
                    </div>

                    <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="planos-destaques">
                        {DESTAQUES_PLANOS.map((d, i) => (
                            <Reveal key={d.valor} delay={i * 0.08} className="h-full">
                                <div className={`flex h-full flex-col justify-between rounded-2xl border p-5 transition-all duration-300 hover:-translate-y-1 ${d.destaque ? "border-laranja-200 bg-laranja-50" : "border-slate-200/80 bg-white shadow-suave"}`}>
                                    <d.icon className={`h-5 w-5 ${d.destaque ? "text-laranja-500" : "text-azul-500"}`} />
                                    <div className="mt-6">
                                        <p className={`font-display font-extrabold tracking-tight ${d.destaque ? "text-2xl text-laranja-500" : "text-lg text-tinta"}`}>{d.valor}</p>
                                        <p className="mt-1 text-[13px] font-medium leading-snug text-tinta-suave">{d.texto}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.1}>
                        <div className="mt-10 text-center">
                            <a
                                href="#planos"
                                data-testid="planos-destaques-cta"
                                className="group inline-flex items-center gap-2 rounded-full bg-azul-500 px-7 py-3.5 text-base font-bold text-white shadow-[0_10px_28px_-8px_rgba(13,82,206,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-azul-600"
                            >
                                Conheça nossos planos <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ================= SOLUÇÕES ================= */}
            <section className="bg-azul-50/60 py-20 md:py-28" data-testid="home-solucoes">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <Reveal><Eyebrow>Nossas soluções</Eyebrow></Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-tinta sm:text-4xl lg:text-5xl" data-testid="solucoes-title">
                                Soluções para simplificar a gestão de benefícios
                            </h2>
                        </Reveal>
                    </div>

                    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        <CardSolucao
                            testid="solucao-card-vt"
                            img="https://static.prod-images.emergentagent.com/jobs/6aa031ac-bbd2-48ac-9d32-a39422d666e7/images/7c635e85fc4810fd02b463813bd9098381c1cb2c5ec9ca75ee9737e2e3907b6a.jpeg"
                            alt="Pessoa passando pela catraca de um ônibus urbano brasileiro com cartão de transporte amarelo"
                            titulo="Vale-Transporte"
                            texto="Uma solução completa para facilitar a gestão do transporte dos colaboradores, com mais praticidade e controle para o RH."
                            cta="Conheça o Vale-Transporte"
                            href="/solucoes#vale-transporte"
                        >
                            <div className="mt-5 space-y-2.5 rounded-2xl border border-azul-100 bg-azul-50/70 p-4">
                                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-azul-700">Dentro do Vale-Transporte</p>
                                <div className="flex items-start gap-2.5">
                                    <Zap className="mt-0.5 h-4 w-4 shrink-0 text-laranja-500" />
                                    <div>
                                        <p className="text-sm font-bold text-tinta">Smart Pix</p>
                                        <p className="text-[13px] leading-snug text-tinta-suave">Uma alternativa prática para disponibilização do benefício através do Pix.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-laranja-500" />
                                    <div>
                                        <p className="text-sm font-bold text-tinta">Smart Libert</p>
                                        <p className="text-[13px] leading-snug text-tinta-suave">Mais liberdade e flexibilidade para a gestão e utilização do benefício de transporte.</p>
                                    </div>
                                </div>
                            </div>
                        </CardSolucao>

                        <CardSolucao
                            testid="solucao-card-vr"
                            img="https://images.unsplash.com/photo-1559136646-8d0d1f46a146?q=80&w=1600&auto=format&fit=crop"
                            alt="Colaboradores fazendo uma refeição no escritório"
                            titulo="Vale-Refeição"
                            texto="Ofereça aos seus colaboradores mais praticidade no dia a dia, enquanto sua empresa mantém uma gestão simples e organizada do benefício."
                            cta="Conheça o Vale-Refeição"
                            href="/solucoes#vale-refeicao"
                        >
                            <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <DestaqueCheck>Gestão simplificada</DestaqueCheck>
                                <DestaqueCheck>Controle por colaborador</DestaqueCheck>
                                <DestaqueCheck>Relatórios e histórico</DestaqueCheck>
                                <DestaqueCheck>Mais eficiência para o RH</DestaqueCheck>
                            </ul>
                        </CardSolucao>

                        <CardSolucao
                            testid="solucao-card-va"
                            img="https://images.unsplash.com/photo-1685640206182-c51b8aa9b686?q=80&w=1600&auto=format&fit=crop"
                            alt="Carrinho de compras de supermercado com alimentos"
                            titulo="Vale-Alimentação"
                            texto="Ofereça um benefício essencial para seus colaboradores com uma gestão moderna e eficiente."
                            cta="Conheça o Vale-Alimentação"
                            href="/solucoes#vale-alimentacao"
                        >
                            <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <DestaqueCheck>Gestão centralizada</DestaqueCheck>
                                <DestaqueCheck>Controle de colaboradores</DestaqueCheck>
                                <DestaqueCheck>Mais praticidade para o RH</DestaqueCheck>
                                <DestaqueCheck>Segurança e eficiência</DestaqueCheck>
                            </ul>
                        </CardSolucao>
                    </div>
                </div>
            </section>

            {/* ================= COMO FUNCIONA ================= */}
            <section className="bg-white py-20 md:py-28" data-testid="home-como-funciona">
                <div className="container-x">
                    <div className="mx-auto max-w-3xl text-center">
                        <Reveal><Eyebrow>Como funciona</Eyebrow></Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-tinta sm:text-4xl lg:text-5xl" data-testid="como-funciona-title">
                                Simples para a empresa. Fácil para o RH.
                            </h2>
                        </Reveal>
                    </div>

                    <div className="relative mt-14" data-testid="como-funciona-passos">
                        <div aria-hidden="true" className="absolute left-0 right-0 top-9 hidden h-px bg-gradient-to-r from-transparent via-azul-200 to-transparent lg:block" />
                        <ol className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5 lg:gap-4">
                            {PASSOS.map((p, i) => (
                                <Reveal key={p.n} delay={i * 0.09}>
                                    <li className="relative text-center lg:text-left">
                                        <div className="relative mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-2xl border border-azul-100 bg-white shadow-suave lg:mx-0">
                                            <p.icon className="h-7 w-7 text-azul-500" strokeWidth={1.8} />
                                            <span className="absolute -right-2 -top-2 rounded-full bg-laranja-500 px-2 py-0.5 font-display text-[11px] font-extrabold text-white">
                                                {p.n}
                                            </span>
                                        </div>
                                        <p className="mt-4 font-display text-base font-bold leading-snug text-tinta">{p.titulo}</p>
                                    </li>
                                </Reveal>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* ================= POR QUE SMART ================= */}
            <section className="bg-azul-50/60 py-20 md:py-28" data-testid="home-por-que-smart">
                <div className="container-x">
                    <div className="max-w-3xl">
                        <Reveal><Eyebrow>Por que Smart?</Eyebrow></Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-tinta sm:text-4xl lg:text-5xl" data-testid="por-que-title">
                                Mais simplicidade para o RH. Mais controle para sua empresa.
                            </h2>
                        </Reveal>
                    </div>

                    <div className="mt-12 grid gap-6 md:grid-cols-2" data-testid="por-que-blocos">
                        {PORQUE.map((b, i) => (
                            <Reveal key={b.titulo} delay={i * 0.08} className="h-full">
                                <div className={`h-full rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-card ${b.grande ? "border-laranja-200 bg-gradient-to-b from-white to-laranja-50/60" : "border-slate-200/80 bg-white shadow-suave"}`}>
                                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${b.grande ? "bg-laranja-500 text-white" : "bg-azul-50 text-azul-600"}`}>
                                        <b.icon className="h-5 w-5" strokeWidth={1.8} />
                                    </div>
                                    {b.grande && (
                                        <p className="mt-6 font-display text-5xl font-extrabold tracking-tight text-laranja-500" data-testid="por-que-preco">
                                            R$ 99<span className="text-2xl">/mês</span>
                                        </p>
                                    )}
                                    <h3 className="mt-4 font-display text-xl font-bold text-tinta sm:text-2xl">{b.titulo}</h3>
                                    <p className="mt-2 max-w-md text-[15px] leading-relaxed text-tinta-medio">{b.texto}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= PLANOS SMART ================= */}
            <section id="planos" className="scroll-mt-24 bg-white py-20 md:py-28" data-testid="home-plano">
                <div className="container-x">
                    <div className="mx-auto max-w-3xl text-center">
                        <Reveal><Eyebrow>Planos Smart</Eyebrow></Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-tinta sm:text-4xl lg:text-5xl" data-testid="plano-title">
                                Tudo o que sua empresa precisa em um só lugar.
                            </h2>
                        </Reveal>
                    </div>

                    <Reveal delay={0.12}>
                        <div className="relative mx-auto mt-12 max-w-4xl overflow-hidden rounded-[2rem] border-2 border-azul-100 bg-white shadow-card" data-testid="plano-card">
                            <div aria-hidden="true" className="absolute left-1/2 top-[-140px] h-72 w-[420px] -translate-x-1/2 rounded-full bg-laranja-200/40 blur-3xl animate-pulse-soft" />
                            <div className="relative grid lg:grid-cols-5">
                                <div className="flex flex-col justify-center border-b border-slate-100 bg-azul-50/60 p-8 lg:col-span-2 lg:border-b-0 lg:border-r lg:p-10">
                                    <span className="inline-flex w-fit items-center rounded-full bg-azul-500 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-white">
                                        Planos Smart
                                    </span>
                                    <p className="mt-6 text-sm font-bold uppercase tracking-[0.14em] text-tinta-suave">a partir de</p>
                                    <p className="font-display text-5xl font-extrabold tracking-tight text-azul-900 lg:text-[3.4rem]" data-testid="plano-preco">
                                        R$ 99<span className="text-2xl text-tinta-suave">/mês</span>
                                    </p>
                                    <p className="mt-2 font-semibold text-laranja-500">Gestão de benefícios sem complicação.</p>
                                    <p className="mt-4 text-sm leading-relaxed text-tinta-medio">
                                        Conheça nossos planos e descubra a melhor forma de simplificar a gestão de benefícios da sua empresa.
                                    </p>
                                    <Link
                                        to="/contato"
                                        data-testid="plano-cta"
                                        className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-azul-500 px-7 py-3.5 text-base font-bold text-white shadow-[0_10px_28px_-8px_rgba(13,82,206,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-azul-600"
                                    >
                                        Conheça nossos planos <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </div>
                                <div className="p-8 lg:col-span-3 lg:p-10">
                                    <p className="text-sm font-bold uppercase tracking-[0.14em] text-azul-700">A plataforma Smart inclui</p>
                                    <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3.5 sm:grid-cols-2">
                                        {PLANO_ITENS.map((item) => (
                                            <li key={item} className="flex items-start gap-2.5 text-sm font-medium text-tinta-medio">
                                                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-azul-50">
                                                    <Check className="h-3 w-3 text-azul-600" strokeWidth={3} />
                                                </span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ================= CTA FINAL ================= */}
            <section className="bg-white pb-20 pt-4 md:pb-28" data-testid="home-cta-final">
                <div className="container-x">
                    <Reveal>
                        <div className="grain relative overflow-hidden rounded-[2.5rem] bg-azul-950 px-6 py-16 text-center md:px-16 md:py-24" data-testid="cta-final-card">
                            <div aria-hidden="true" className="absolute -left-20 top-0 h-72 w-72 rounded-full bg-azul-500/30 blur-3xl" />
                            <div aria-hidden="true" className="absolute -bottom-24 -right-10 h-80 w-80 rounded-full bg-laranja-500/25 blur-3xl" />
                            <div className="relative z-10 mx-auto max-w-3xl">
                                <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="cta-final-title">
                                    Vamos simplificar a gestão dos benefícios da sua empresa?
                                </h2>
                                <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
                                    Conheça a Smart Benefícios e descubra uma nova forma de administrar seus benefícios.
                                </p>
                                <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                                    <Link
                                        to="/contato"
                                        data-testid="cta-final-especialista"
                                        className="inline-flex items-center justify-center gap-2 rounded-full bg-laranja-500 px-7 py-3.5 text-base font-bold text-white shadow-laranja transition-all duration-200 hover:-translate-y-0.5 hover:bg-laranja-600"
                                    >
                                        Fale com um especialista
                                    </Link>
                                    <a
                                        href="#planos"
                                        data-testid="cta-final-solucoes"
                                        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:border-white hover:bg-white/10"
                                    >
                                        Conheça nossos planos
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
